import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { Button } from 'components/patterns'
import Icon from 'components/patterns/Icon'
import strings from 'utils/strings'

/**
 * ActivationConfirmationPanel (PBI 150666)
 * Confirmation panel for lifecycle status changes (Activate/Obsolete)
 * Displayed in the drawer when user clicks on a lifecycle status button
 *
 * Bug 479122: Updated to match Features page confirmation panel style
 * Bug 479123: Icon uses warning_solid_black (yellow triangle with black exclamation mark)
 * - Icon: warning_solid_black (yellow triangle, black exclamation)
 * - Header: 16px bold
 * - Body: 14px normal
 * - Buttons: Right-aligned at bottom
 */

// Bug 479123 Issues 13, 14: Content at top, buttons at bottom
// Uses same pattern as ConfirmationOverlayContainer and Features Editor
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% + 40px);
  margin: -20px;
  padding: 20px;
  width: calc(100% + 40px);
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`

// Icon indicator - use warning_solid_black icon with yellow triangle and black exclamation mark
// Bug 479123: Apply specific fills for each path (yellow triangle, black exclamation)
const IconIndicator = styled(Icon)`
  font-size: 18px;
  margin-top: 2px;
  margin-right: 10px;
  flex-shrink: 0;
  width: 24px;
  height: 18px;

  && {
    path:first-child {
      fill: rgb(239, 177, 21);
    }
    path:last-child {
      fill: rgb(7, 7, 15);
    }
  }
`

const TextWrapper = styled.div`
  flex-grow: 1;
`

const HeaderText = styled.h3`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  margin: 0 0 8px 0;
  color: ${themeColor('blackSecondary')};
`

const BodyText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin: 0;
  color: ${themeColor('blackSecondary')};
`

// Bug 479123 Issue 4, 14: Buttons at bottom with dividing line above
// Cancel on left, primary action on right (matching Figma design)
const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
  border-top: thin solid ${themeColor('greyPrimary')};
  margin-top: auto;
`

const ActivationConfirmationPanel = ({
  header,
  body,
  confirmText,
  onConfirm,
  onCancel,
  isLoading,
}) => (
  <Container>
    <ContentWrapper>
      <IconIndicator icon="warning_solid_black" />
      <TextWrapper>
        <HeaderText>{header}</HeaderText>
        <BodyText>{body}</BodyText>
      </TextWrapper>
    </ContentWrapper>
    <ButtonGroup>
      <Button type="secondary" onClick={onCancel} disabled={isLoading}>
        {strings.cancel || 'Cancel'}
      </Button>
      <Button
        type="primary"
        onClick={onConfirm}
        disabled={isLoading}
        loading={isLoading}
      >
        {confirmText}
      </Button>
    </ButtonGroup>
  </Container>
)

ActivationConfirmationPanel.propTypes = {
  header: PropTypes.string.isRequired,
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  confirmText: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

ActivationConfirmationPanel.defaultProps = {
  isLoading: false,
}

export default ActivationConfirmationPanel
